import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { onSurvival, type SurvivalEvent } from '../net/liveSession';
import { useGameStore } from '../store/gameStore';
import { audio } from '../game/audio/AudioEngine';
import { buzz, HAPTIC } from '../util/haptics';

/**
 * SurvivalToast — pops a celebratory chip when the player has stayed
 * alive long enough to cross a server-side survival threshold.
 *
 * Triggers (per life):
 *   60 s  → "you survived 60 seconds"
 *   120 s → "two minutes alive"
 *   300 s → "five-minute champion"
 *
 * The 60 s threshold lands at the deepest cliff identified in the
 * retention audit (66 % of players quit between 30 s and 1 min).
 * Surfacing a celebratory cue exactly there anchors them — "I just
 * did something" instead of drifting toward close-tab.
 *
 * Visual: emerald-tinted chip, slightly smaller than MilestoneToast
 * so multiple events stacking near each other don't visually compete.
 * Lifetime: 2 s. Plays a chime + haptic pulse.
 */
interface ActiveToast {
  id: number;
  ms: number;
}

let nextToastId = 1;
const MAX_VISIBLE = 1;

function labelFor(ms: number): { headline: string; sub: string } {
  switch (ms) {
    case 60_000:  return { headline: '60 seconds',  sub: 'first minute' };
    case 120_000: return { headline: '2 minutes',    sub: 'still going' };
    case 300_000: return { headline: '5 minutes',    sub: 'champion run' };
    default:      return { headline: Math.round(ms / 1000) + 's', sub: 'survived' };
  }
}

export function SurvivalToast() {
  const [active, setActive] = useState<ActiveToast[]>([]);
  const playerHue = useGameStore((s) => s.hud.playerHue);
  // Suppress while dead — same rationale as MilestoneToast: nothing
  // should overlap the death modal.
  const alive = useGameStore((s) => s.hud.alive);

  useEffect(() => {
    return onSurvival((e: SurvivalEvent) => {
      setActive((prev) => {
        const trimmed = prev.length >= MAX_VISIBLE ? prev.slice(1) : prev;
        return [...trimmed, { id: nextToastId++, ms: e.ms }];
      });
      try { audio.spawn(playerHue); } catch { /* noop */ }
      buzz(HAPTIC.reward);
      const idAtFire = nextToastId - 1;
      window.setTimeout(() => {
        setActive((prev) => prev.filter((t) => t.id !== idAtFire));
      }, 2000);
    });
  }, [playerHue]);

  if (!alive) return null;

  return (
    <div className="absolute top-44 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center gap-2">
      <AnimatePresence>
        {active.map((t) => {
          const { headline, sub } = labelFor(t.ms);
          return (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, scale: 0.85, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -8, transition: { duration: 0.2 } }}
              transition={{ type: 'spring', stiffness: 360, damping: 24 }}
              style={{
                background:
                  'linear-gradient(180deg, rgba(110,231,183,0.22) 0%, rgba(52,211,153,0.14) 100%)',
                border: '1px solid rgba(110,231,183,0.55)',
                boxShadow:
                  '0 0 20px rgba(110,231,183,0.30), 0 0 50px rgba(52,211,153,0.15), inset 0 1px 0 rgba(255,255,255,0.16)',
              }}
              className="px-4 py-2 rounded-2xl text-center backdrop-blur-md"
            >
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-sm" aria-hidden>⏱</span>
                <span
                  className="text-xl font-extrabold tabular-nums"
                  style={{
                    fontFamily: '"Inter", "Space Grotesk", system-ui, sans-serif',
                    color: 'rgb(187 247 208)',
                    textShadow: '0 0 10px rgba(110,231,183,0.85)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {headline}
                </span>
              </div>
              <div className="text-[8px] uppercase tracking-[0.32em] text-emerald-200/80 font-bold mt-0.5">
                {sub}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
